import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { ShieldCheck, KeyRound, Download, AlertTriangle } from "lucide-react";
import { api } from "@/lib/api";
import { generateAdminKeypair } from "@/lib/e2eCrypto";
import useSEO from "@/hooks/useSEO";

// Page /admin/setup-e2e — génération de la paire de clés E2E.
// La clé privée est chiffrée avec la phrase de passe puis proposée en
// téléchargement JSON. Rien n'est envoyé au serveur sauf la clé publique.
export default function AdminSetupE2E() {
    useSEO({ title: "Configuration E2E · Admin", canonical: "" });
    const navigate = useNavigate();
    const [passphrase, setPassphrase] = useState("");
    const [confirm, setConfirm] = useState("");
    const [busy, setBusy] = useState(false);
    const [done, setDone] = useState(false);

    const strong = passphrase.length >= 12 && /[A-Z]/.test(passphrase) && /[0-9]/.test(passphrase);

    const generate = async () => {
        if (!strong) {
            toast.error("Phrase de passe trop faible (12 caractères min, 1 majuscule, 1 chiffre).");
            return;
        }
        if (passphrase !== confirm) {
            toast.error("Les phrases de passe ne correspondent pas.");
            return;
        }
        const token = (() => {
            try {
                const s = JSON.parse(localStorage.getItem("admin_session") || "{}");
                return s.token || null;
            } catch { return null; }
        })();
        if (!token) {
            toast.error("Connectez-vous à /admin d'abord.");
            navigate("/admin");
            return;
        }
        setBusy(true);
        try {
            const { publicJwk, backup } = await generateAdminKeypair(passphrase);

            // Upload public key to server
            await api.post(
                "/admin/e2e/setup",
                { pubkey_jwk: publicJwk, algorithm: "ECDH-P384-AES256GCM" },
                { headers: { "X-Admin-Token": token } },
            );

            // Trigger download of the encrypted backup
            const blob = new Blob([JSON.stringify(backup, null, 2)], { type: "application/json" });
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = `massoclic-e2e-backup-${new Date().toISOString().slice(0, 10)}.json`;
            a.click();
            URL.revokeObjectURL(url);

            setDone(true);
            toast.success("Clé E2E générée. Sauvegarde téléchargée.");
        } catch (e) {
            toast.error("Erreur lors de la génération : " + (e?.message || e));
        } finally {
            setBusy(false);
        }
    };

    return (
        <main data-testid="admin-setup-e2e-page" className="pt-32 md:pt-40 pb-20 min-h-screen">
            <div className="max-w-2xl mx-auto px-6 md:px-12">
                <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white border border-[#E8E2D9] mb-6">
                    <ShieldCheck size={14} className="text-[#3D8C95]" />
                    <span className="text-xs tracking-[0.2em] uppercase text-[#6E6862]">
                        Configuration Zero-Knowledge
                    </span>
                </div>
                <h1 className="font-display text-4xl md:text-5xl font-light text-[#2C2A29] tracking-tight leading-[1.05]">
                    Chiffrement de{" "}
                    <span className="italic text-[#E07A5F]">bout en bout</span>
                </h1>
                <p className="mt-4 text-[#6E6862] leading-relaxed">
                    Générez votre paire de clés cryptographiques. Le serveur ne verra
                    JAMAIS votre clé privée — elle est dérivée de votre phrase de passe
                    et chiffrée dans un fichier de sauvegarde à conserver précieusement.
                </p>

                {done ? (
                    <div data-testid="e2e-setup-done" className="mt-8 rounded-3xl border-2 border-[#3D8C95] bg-white p-6 md:p-8">
                        <ShieldCheck size={32} className="text-[#3D8C95] mb-3" />
                        <h2 className="font-display text-2xl text-[#2C2A29]">Chiffrement E2E activé ✓</h2>
                        <p className="mt-2 text-sm text-[#6E6862] leading-relaxed">
                            Votre fichier de sauvegarde a été téléchargé. <b>Rangez-le en lieu sûr</b> —
                            sans lui + votre phrase de passe, il sera impossible de lire les nouvelles
                            réservations chiffrées.
                        </p>
                        <ul className="mt-4 text-sm text-[#3A3735] space-y-2">
                            <li>• Cloud personnel chiffré (recommandé)</li>
                            <li>• Clé USB gardée en coffre-fort</li>
                            <li>• Impression papier du contenu JSON (double redondance)</li>
                        </ul>
                        <Button
                            onClick={() => navigate("/admin")}
                            className="mt-6 rounded-full bg-[#E07A5F] hover:bg-[#D06A4F] text-white px-6 py-5"
                        >
                            Retour au tableau de bord
                        </Button>
                    </div>
                ) : (
                    <div className="mt-8 rounded-3xl border border-[#E8E2D9] bg-white p-6 md:p-8">
                        <div className="flex items-start gap-3 mb-6 p-4 rounded-2xl bg-[#C44569]/10 border border-[#C44569]/30">
                            <AlertTriangle size={18} className="text-[#C44569] shrink-0 mt-0.5" />
                            <p className="text-sm text-[#3A3735] leading-relaxed">
                                <b>Attention</b> — Si vous perdez votre phrase de passe ET le fichier
                                de sauvegarde, les réservations chiffrées seront perdues à jamais.
                                Ni Emergent, ni personne d'autre ne pourra les récupérer. C'est le prix
                                de la sécurité absolue.
                            </p>
                        </div>

                        <div className="space-y-4">
                            <div>
                                <Label htmlFor="passphrase" className="text-sm font-medium text-[#2C2A29]">
                                    Phrase de passe secrète
                                </Label>
                                <Input
                                    id="passphrase"
                                    data-testid="e2e-passphrase"
                                    type="password"
                                    value={passphrase}
                                    onChange={(e) => setPassphrase(e.target.value)}
                                    placeholder="12 caractères min · 1 majuscule · 1 chiffre"
                                    className="mt-2 rounded-full"
                                    autoComplete="new-password"
                                />
                                {passphrase && (
                                    <p className={`mt-2 text-xs ${strong ? "text-[#3D8C95]" : "text-[#C44569]"}`}>
                                        {strong ? "✓ Phrase forte" : "Trop faible"}
                                    </p>
                                )}
                            </div>
                            <div>
                                <Label htmlFor="confirm" className="text-sm font-medium text-[#2C2A29]">
                                    Confirmer la phrase
                                </Label>
                                <Input
                                    id="confirm"
                                    data-testid="e2e-confirm"
                                    type="password"
                                    value={confirm}
                                    onChange={(e) => setConfirm(e.target.value)}
                                    className="mt-2 rounded-full"
                                    autoComplete="new-password"
                                />
                            </div>

                            <Button
                                onClick={generate}
                                disabled={busy || !strong || passphrase !== confirm}
                                data-testid="e2e-generate-btn"
                                className="w-full rounded-full bg-[#3D8C95] hover:bg-[#2C6D75] text-white px-6 py-6 mt-4 disabled:opacity-40"
                            >
                                <KeyRound size={16} className="mr-2" />
                                {busy ? "Génération..." : "Générer la clé et télécharger la sauvegarde"}
                            </Button>

                            <div className="text-xs text-[#6E6862] leading-relaxed mt-4">
                                <p><b>Ce qui va se passer :</b></p>
                                <ol className="list-decimal ml-5 mt-2 space-y-1">
                                    <li>Une paire de clés ECDH P-384 est générée dans votre navigateur</li>
                                    <li>La clé privée est chiffrée en AES-256-GCM avec votre phrase de passe</li>
                                    <li>Un fichier JSON de sauvegarde est téléchargé sur votre ordinateur</li>
                                    <li>Seule la clé PUBLIQUE est envoyée au serveur</li>
                                    <li>Toutes les nouvelles réservations seront chiffrées automatiquement</li>
                                </ol>
                            </div>
                        </div>
                    </div>
                )}

                <div className="mt-6 text-center">
                    <button
                        onClick={() => navigate("/admin")}
                        className="text-sm text-[#3D8C95] hover:text-[#2C6D75] underline underline-offset-4"
                    >
                        ← Retour au tableau de bord
                    </button>
                </div>
            </div>
        </main>
    );
}
